/**
 * Phase E1.a — Reusable error banner for the CSV export flow.
 *
 * Two visual idioms exist across the IA's 7 Download-CSV call sites:
 *
 *  - **`inline`** (Staff / Students / Parents / Admissions / Trash) —
 *    a compact `div` with `role="alert"` rendered above the
 *    `<DataTable>`. Uses a text "Dismiss" button. This is the toolbar
 *    family of pages, where the error sits near the toolbar that
 *    triggered the click.
 *  - **`card`** (Workload / Calendar) — a full-bleed `Card` with
 *    `role="alert"` rendered between `<PageHeader>` and the body. Uses
 *    an icon-only Dismiss with an explicit `aria-label="Dismiss CSV
 *    export error"`. This is the header-actions family of pages, where
 *    the error is page-level rather than table-level.
 *
 * Pages pass their existing `data-testid` so we never break a test by
 * renaming the alert. Pages also keep control of *where* the banner
 * sits in the DOM tree — this component never imposes a layout
 * decision; it only renders the visual shape that matches the page's
 * surrounding chrome.
 *
 * The component intentionally doesn't own the dismiss-state machine:
 * the parent (or the companion `useCsvExport` hook) tracks
 * `exportError` and calls back via `onDismiss`. Keeps the banner
 * stateless and trivially mockable in tests.
 */

import { AlertCircle, X } from "lucide-react";
import { Button, Card, CardContent, cn } from "@eduviaos/ui-kit";

export type ExportCsvErrorBannerVariant = "inline" | "card";

export interface ExportCsvErrorBannerProps {
  /**
   * Error message to render. The banner only renders when this is
   * non-null — pages can spread it unconditionally and rely on the
   * component to do the visibility check.
   */
  error: string | null;
  /**
   * Click handler for the Dismiss button. Pages typically wire this to
   * the `dismissError` callback from `useCsvExport`.
   */
  onDismiss: () => void;
  /**
   * Page-specific `data-testid` so existing tests keep selecting the
   * right element after the migration (e.g.
   * `admissions-calendar-export-error`).
   */
  testid: string;
  /**
   * `inline` mirrors the toolbar-page idiom (`div` + text Dismiss).
   * `card` mirrors the header-page idiom (`Card` + icon Dismiss).
   */
  variant: ExportCsvErrorBannerVariant;
  /**
   * Optional `className` so pages can fine-tune spacing (e.g. `mt-6`
   * vs `mb-4` depending on what sits above the banner).
   */
  className?: string;
}

export function ExportCsvErrorBanner({
  error,
  onDismiss,
  testid,
  variant,
  className,
}: ExportCsvErrorBannerProps) {
  if (!error) return null;

  if (variant === "card") {
    return (
      <Card
        className={cn("border-destructive/40 bg-destructive/5", className)}
        role="alert"
        data-testid={testid}
      >
        <CardContent className="flex flex-wrap items-center gap-3 py-3">
          <AlertCircle className="text-destructive size-4 shrink-0" aria-hidden="true" />
          <p className="text-foreground flex-1 text-sm">{error}</p>
          <Button
            variant="ghost"
            size="sm"
            onClick={onDismiss}
            aria-label="Dismiss CSV export error"
          >
            <X className="size-4" aria-hidden="true" />
            Dismiss
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div
      role="alert"
      data-testid={testid}
      className={cn(
        "border-destructive/30 bg-destructive/5 text-destructive flex items-start gap-2 rounded-md border px-3 py-2 text-xs",
        className,
      )}
    >
      <AlertCircle aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
      <span className="flex-1">{error}</span>
      <Button variant="ghost" size="sm" className="h-auto px-2 py-0.5 text-xs" onClick={onDismiss}>
        Dismiss
      </Button>
    </div>
  );
}
